import { useState } from 'react';
import { MessageCircle, X, Send } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ChatWidget = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [message, setMessage] = useState('');
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!message.trim()) return;

    setLoading(true);
    try {
      const response = await axios.post(`${API}/chat`, {
        message,
        email: email || undefined,
      });
      toast.success(response.data.message);
      setMessage('');
      setEmail('');
    } catch (error) {
      toast.error('Failed to send message. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {/* Chat Button */}
      {!isOpen && (
        <button
          onClick={() => setIsOpen(true)}
          className="fixed bottom-6 right-6 z-[9998] w-16 h-16 bg-teal-500 hover:bg-teal-600 text-white rounded-full shadow-2xl flex items-center justify-center transition-all duration-300 hover:scale-110"
          data-testid="chat-widget-open-btn"
        >
          <MessageCircle className="w-7 h-7" />
        </button>
      )}

      {/* Chat Window */}
      {isOpen && (
        <div
          className="fixed bottom-6 right-6 z-[9998] w-96 max-w-[calc(100vw-2rem)] bg-white rounded-2xl shadow-2xl border border-slate-200 overflow-hidden"
          data-testid="chat-widget-window"
        >
          {/* Header */}
          <div className="bg-gradient-to-r from-teal-500 to-teal-600 p-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                <MessageCircle className="w-5 h-5 text-white" />
              </div>
              <div>
                <h3 className="font-bold text-white">Ashvya Digital Media</h3>
                <p className="text-xs text-teal-50">We typically reply instantly</p>
              </div>
            </div>
            <button
              onClick={() => setIsOpen(false)}
              className="text-white hover:bg-white/20 p-2 rounded-lg transition-colors"
              data-testid="chat-widget-close-btn"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Messages */}
          <div className="p-6 h-64 overflow-y-auto bg-slate-50">
            <div className="bg-white rounded-2xl p-4 shadow-sm mb-4">
              <p className="text-slate-700">
                👋 Hi there! Welcome to Ashvya Digital Media. How can we help you grow your business today?
              </p>
            </div>
            <div className="bg-white rounded-2xl p-4 shadow-sm">
              <p className="text-slate-700 mb-2">
                We specialize in:
              </p>
              <ul className="text-sm text-slate-600 space-y-1 ml-4">
                <li>• Social Media Marketing</li>
                <li>• Google & Meta Ads</li>
                <li>• Lead Generation</li>
                <li>• Content Creation</li>
              </ul>
            </div>
          </div>

          {/* Input Form */}
          <form onSubmit={handleSubmit} className="p-4 border-t border-slate-200 bg-white">
            <input
              type="email"
              placeholder="Your email (optional)"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full px-4 py-2 rounded-xl border border-slate-300 focus:outline-none focus:ring-2 focus:ring-teal-500 mb-2 text-sm"
              data-testid="chat-email-input"
            />
            <div className="flex gap-2">
              <input
                type="text"
                placeholder="Type your message..."
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                required
                className="flex-1 px-4 py-3 rounded-xl border border-slate-300 focus:outline-none focus:ring-2 focus:ring-teal-500"
                data-testid="chat-message-input"
              />
              <button
                type="submit"
                disabled={loading || !message.trim()}
                className="bg-teal-500 hover:bg-teal-600 text-white p-3 rounded-xl transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="chat-send-btn"
              >
                <Send className="w-5 h-5" />
              </button>
            </div>
            <p className="text-xs text-slate-500 mt-2">
              Or call us directly: <a href="tel:9068922387" className="text-teal-600 font-semibold">9068922387</a>
            </p>
          </form>
        </div>
      )}
    </>
  );
};

export default ChatWidget;